import { createMultiSelectSection, createSingleSelectSection } from '@aimsos/ui';
import { catalogCategoryLabel, CATEGORY_I18N_KEYS } from './catalogCategory.js';
import {
  BarChart3,
  Boxes,
  Code2,
  Database,
  DollarSign,
  FileText,
  MessageSquare,
  Network,
  PenLine,
  RefreshCw,
  ShieldCheck,
  Target,
  Ticket,
  Users,
  Zap,
} from 'lucide-react';

/**
 * Presentation meta for the Marketplace Filters slideout (CT-4168), mirroring the
 * prototype's five sections. Icon colors are the prototype's measured tones mapped
 * to the nearest dark-aware Tailwind tokens (tokens only — no inline hex on text).
 * Category `value`s are the catalog English keys (`CATEGORY_I18N_KEYS`) so they
 * filter `item.category` directly. Order matches the prototype.
 */
const CATEGORY_CHROME = {
  Communication: { Icon: MessageSquare, color: 'text-blue-600 dark:text-blue-400' },
  'Productivity & Docs': { Icon: FileText, color: 'text-violet-600 dark:text-violet-400' },
  'CRM & Sales': { Icon: Target, color: 'text-emerald-600 dark:text-emerald-400' },
  'Ticketing & ITSM': { Icon: Ticket, color: 'text-yellow-500 dark:text-yellow-400' },
  'Data & Analytics': { Icon: BarChart3, color: 'text-cyan-600 dark:text-cyan-400' },
  Storage: { Icon: Database, color: 'text-pink-600 dark:text-pink-500' },
  'Dev & Code': { Icon: Code2, color: 'text-red-600 dark:text-red-400' },
  'HR & People': { Icon: Users, color: 'text-teal-600 dark:text-teal-400' },
  'Finance & ERP': { Icon: DollarSign, color: 'text-amber-600 dark:text-amber-400' },
  'Security & Observability': { Icon: ShieldCheck, color: 'text-violet-700 dark:text-violet-500' },
  'Custom / Build your own': { Icon: PenLine, color: 'text-gray-500 dark:text-gray-400' },
};

/** Capability taxonomy — labels come from `integrations.card.capability.*`. */
const CAPABILITY_META = [
  { value: 'tool', Icon: Zap, color: 'text-violet-600 dark:text-violet-400' },
  { value: 'data_sync', Icon: RefreshCw, color: 'text-emerald-600 dark:text-emerald-400' },
  { value: 'mcp', Icon: Boxes, color: 'text-cyan-600 dark:text-cyan-400' },
];

/** Connection state — rendered as the option-row semantic dot (green / grey). */
const CONNECTION_META = [
  { value: 'connected', color: '#10b981' },
  { value: 'not_connected', color: '#9ca3af' },
];

/** "Used in studio" (single-select) — icon + description subtext. */
const STUDIO_META = [
  { value: 'governance', Icon: ShieldCheck, color: 'text-emerald-600 dark:text-emerald-400' },
  { value: 'agentic', Icon: Network, color: 'text-violet-600 dark:text-violet-400' },
  { value: 'workforce', Icon: MessageSquare, color: 'text-blue-600 dark:text-blue-400' },
];

/** Source (single-select) — label + description, no icon (matches prototype). */
const SOURCE_VALUES = ['official', 'partner', 'private'];

/** Icon + text label for an option row. */
const iconLabel = (Icon, colorClass, text) => (
  <span className="flex items-center gap-2">
    <Icon size={14} className={colorClass} aria-hidden />
    <span className="truncate">{text}</span>
  </span>
);

/**
 * Builds the five collapsible sections for the Marketplace Filters slideout
 * (CT-4168), matching the prototype: Category / Capability / Connection state
 * (multi-select) and Used in studio / Source (single-select). Each section binds
 * to the DRAFT state; nothing is applied until the slideout's Apply. Section and
 * option labels come from i18n via `t`.
 *
 * @param {object} draft - current draft filter values (arrays for multi, string for single)
 * @param {(key: string, value: string[] | string) => void} setField
 * @param {(key: string) => string} t
 */
export const buildMarketplaceFilterSlots = (draft, setField, t) => [
  createMultiSelectSection({
    id: 'category',
    title: t('integrations.marketplace.filters.sections.category'),
    value: draft.category ?? [],
    maxVisible: Object.keys(CATEGORY_I18N_KEYS).length,
    options: Object.keys(CATEGORY_I18N_KEYS).map((value) => {
      const { Icon, color } = CATEGORY_CHROME[value];
      return {
        value,
        label: iconLabel(Icon, color, catalogCategoryLabel(value, t)),
      };
    }),
    onChange: (v) => setField('category', v),
  }),
  createMultiSelectSection({
    id: 'capability_types',
    title: t('integrations.marketplace.filters.sections.capability'),
    value: draft.capability_types ?? [],
    options: CAPABILITY_META.map(({ value, Icon, color }) => ({
      value,
      label: iconLabel(Icon, color, t(`integrations.card.capability.${value}`)),
    })),
    onChange: (v) => setField('capability_types', v),
  }),
  createMultiSelectSection({
    id: 'connection_state',
    title: t('integrations.marketplace.filters.sections.connectionState'),
    value: draft.connection_state ?? [],
    options: CONNECTION_META.map(({ value, color }) => ({
      value,
      color,
      label: t(`integrations.marketplace.filters.connection.${value}`),
    })),
    onChange: (v) => setField('connection_state', v),
  }),
  createSingleSelectSection({
    id: 'used_in_studio',
    title: t('integrations.marketplace.filters.sections.usedInStudio'),
    value: draft.used_in_studio ?? '',
    allValue: '',
    options: STUDIO_META.map(({ value, Icon, color }) => ({
      value,
      label: iconLabel(Icon, color, t(`integrations.marketplace.filters.studio.${value}.label`)),
      description: t(`integrations.marketplace.filters.studio.${value}.desc`),
    })),
    onChange: (v) => setField('used_in_studio', v),
  }),
  createSingleSelectSection({
    id: 'source',
    title: t('integrations.marketplace.filters.sections.source'),
    value: draft.source ?? '',
    allValue: '',
    options: SOURCE_VALUES.map((value) => ({
      value,
      label: t(`integrations.marketplace.filters.source.${value}.label`),
      description: t(`integrations.marketplace.filters.source.${value}.desc`),
    })),
    onChange: (v) => setField('source', v),
  }),
];
